import classNames from 'classnames';
import { nl } from 'date-fns/locale/nl';
import { FocusEvent } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Clock, XCircle } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { dateFormats, useLocale } from '../locale';
import { Button } from './Button';
import { Icon } from './Icon';
import { Input } from './Input';
import { InputWrapper } from './InputWrapper';

registerLocale('nl', nl);

interface TimeInputProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  optional?: boolean;
  id?: string;
  ariaInvalid?: boolean;
}

export function TimeInput({
  value,
  onChange,
  onBlur,
  disabled,
  optional,
  id,
  ariaInvalid,
}: TimeInputProps) {
  const { t } = useTranslation();
  const { locale, formatDate, parseDate } = useLocale();

  const handleChange = (value: Date | null) => {
    onChange?.(value !== null ? formatDate(value, 'internal_time') : null);
  };

  const selected = value ? parseDate(value, 'internal_time') : null;

  return (
    <InputWrapper>
      <label htmlFor={id}>
        <Icon className={classNames(value && !disabled ? 'text-slate-800' : 'text-slate-500')}>
          <Clock />
        </Icon>
      </label>
      <DatePicker
        selected={selected}
        onChange={handleChange}
        onBlur={onBlur}
        customInput={<Input className="pl-9" />}
        locale={locale}
        dateFormat={dateFormats[locale].input_time}
        showTimeSelect
        showTimeSelectOnly
        autoComplete="off"
        placeholderText={t('shared:date_input.time_format_placeholder')}
        id={id}
        disabled={disabled}
        ariaInvalid={ariaInvalid ? 'true' : undefined}
        shouldCloseOnSelect
      />
      {optional && value && (
        <Button
          onClick={() => onChange?.(null)}
          variant="tertiary"
          className="relative text-slate-400 hover:text-slate-800 !border-transparent"
          aria-label={t('shared:filter.clear')}
        >
          <Icon>
            <XCircle />
          </Icon>
        </Button>
      )}
    </InputWrapper>
  );
}
